import React, { useEffect, useState } from "react";
import { Layout, Row, Col, Spin, message, Alert } from "antd";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import FullPageLayout from "../../../../shared-components/layout/FullPageLayout";
import TicketDetailView from "../components/TicketDetailView.js";
import {
  adminGetSupportMessageByTicketIdAction,
  adminGetSupportTicketByIdAction,
} from "../../../../store/action/admin/supportTicketActions.js";
import { TranslatorNavigationTag } from "../../../../languageTranslator/LanguageTranslatorTags";
import CustomLoaderIcon from "../../../../Theme/CustomIcons/Icons/Essetional/CustomLoaderIcon.js";

function SupportDetailPage() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const tokenInfoList = useSelector((state) => state.tokenInfoList);
  const token = tokenInfoList?.info?.token;
  const companyId = tokenInfoList?.info?.company?._id || tokenInfoList?.info?.companyId;

  const ticketResponse = useSelector((state) => state.adminGetSupportTicketByIdList);
  const messagesResponse = useSelector(
    (state) => state.adminGetSupportMessageByTicketIdList,
  );

  console.log("DIAGNOSTIC - ticketResponse:", ticketResponse);
  console.log("DIAGNOSTIC - messagesResponse:", messagesResponse);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const ticketData = ticketResponse?.info || null;

  const extractMessages = () => {
    if (!messagesResponse) return [];

    if (messagesResponse?.info && Array.isArray(messagesResponse.info)) {
      return messagesResponse.info;
    }

    if (messagesResponse?.data && Array.isArray(messagesResponse.data)) {
      return messagesResponse.data;
    }

    if (Array.isArray(messagesResponse)) {
      return messagesResponse;
    }

    return [];
  };

  const messages = extractMessages();

  const breadcrumbItems = [
    {
      title: (
        <div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
          <TranslatorNavigationTag>Support Center</TranslatorNavigationTag>
        </div>
      ),
    },
    {
      title: (
        <Link
          to="/admin/support-listing"
          style={{ display: "flex", gap: "5px", alignItems: "center" }}>
          <TranslatorNavigationTag>All Tickets</TranslatorNavigationTag>
        </Link>
      ),
    },
    {
      title: (
        <span style={{ fontWeight: 500 }}>
          {`Ticket #${ticketData?.data?.ticketId || "TCK"}`}
        </span>
      ),
    },
  ];
  useEffect(() => {
    const fetchTicketDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        if (ticketId) {
          let decryptedTicketId = ticketId;
          try {
            decryptedTicketId = atob(ticketId);
          } catch (e) {
            console.error("Failed to decrypt ticket ID, using raw ID", e);
          }
          await Promise.all([
            dispatch(
              adminGetSupportTicketByIdAction(decryptedTicketId, token, companyId, true),
            ),
            dispatch(
              adminGetSupportMessageByTicketIdAction(
                decryptedTicketId,
                token,
                companyId,
                true,
              ),
            ),
          ]);
        } else {
          setError(
            "Missing required parameters (ticketId, token, or companyId)",
          );
          message.error("Unable to load ticket details");
        }
      } catch (err) {
        console.error("❌ Failed to fetch ticket details:", err);
        const errorMessage = err.message || "Failed to load ticket details";
        setError(errorMessage);
        message.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchTicketDetails();
  }, [ticketId, token, companyId, dispatch]);

  const handleBackToList = () => {
    navigate("/admin/support-listing");
  };

  if (loading) {
    return (
      <Layout color="primary" style={{ height: "100vh" }}>
        <FullPageLayout name="Ticket Details" breadcrumbItems={breadcrumbItems}>
          <div style={{ display: "flex", flex: 1, height: "60vh", width: "100%", justifyContent: "center", alignItems: "center" }}>
            <CustomLoaderIcon size="large" />
          </div>
        </FullPageLayout>
      </Layout>
    );
  }

  if (error && !ticketData) {
    return (
      <Layout color="primary" style={{ height: "100vh" }}>
        <FullPageLayout name="Ticket Details" breadcrumbItems={breadcrumbItems}>
          <Row gutter={[0, 16]} align="middle">
            <Col span={24}>
              <Alert
                message="Error Loading Ticket"
                description={error}
                type="error"
                showIcon
                action={
                  <button
                    onClick={() => window.location.reload()}
                    style={{
                      background: "#ff4d4f",
                      color: "#fff",
                      border: "none",
                      padding: "6px 16px",
                      borderRadius: 6,
                      cursor: "pointer",
                      fontWeight: 600,
                    }}>
                    Retry
                  </button>
                }
                style={{ margin: "20px auto", maxWidth: 600 }}
              />

              <div style={{ textAlign: "center", marginTop: 20 }}>
                <button
                  onClick={handleBackToList}
                  style={{
                    background: "#1890ff",
                    color: "#fff",
                    border: "none",
                    padding: "8px 20px",
                    borderRadius: 6,
                    cursor: "pointer",
                    fontWeight: 500,
                  }}>
                  ← Back to Support List
                </button>
              </div>
            </Col>
          </Row>
        </FullPageLayout>
      </Layout>
    );
  }

  // ✅ No data state
  if (!ticketData) {
    return (
      <Layout color="primary" style={{ height: "100vh" }}>
        <FullPageLayout name="Ticket Details" breadcrumbItems={breadcrumbItems}>
          <Row gutter={[0, 16]} align="middle">
            <Col span={24}>
              <Alert
                message="No Data Found"
                description="This ticket may have been deleted or you don't have permission to view it."
                type="warning"
                showIcon
                style={{ margin: "20px auto", maxWidth: 600 }}
              />

              <div style={{ textAlign: "center", marginTop: 20 }}>
                <button
                  onClick={handleBackToList}
                  style={{
                    background: "#1890ff",
                    color: "#fff",
                    border: "none",
                    padding: "8px 20px",
                    borderRadius: 6,
                    cursor: "pointer",
                    fontWeight: 500,
                  }}>
                  ← Back to Support List
                </button>
              </div>
            </Col>
          </Row>
        </FullPageLayout>
      </Layout>
    );
  }

  return (
    <Layout color="primary" style={{ height: "100vh" }}>
      <FullPageLayout name="Ticket Details" breadcrumbItems={breadcrumbItems}>
        <Row gutter={[0, 16]} align="middle">
          <Col span={24}>
            <TicketDetailView
              ticket={ticketData}
              initialMessages={messages}
              onBack={handleBackToList}
            />
          </Col>
        </Row>
      </FullPageLayout>
    </Layout>
  );
}

export default SupportDetailPage;
